import { useNavigate } from 'react-router-dom'
import { experienceBuyPath } from '../../lib/experienceBuyPath'
import { Car, Clock, Infinity as InfinityIcon, Ticket } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import type { ExperienceProduct } from '../../services/api'
import { formatDuration } from '../../utils/experienceSlot'

/**
 * 体験タブの中身 (#1248/#1292)。店が持つ体験商品 (チケット) を一覧し、各カードから
 * 前払い購入へ。時間指定は購入ページで枠を選ぶ。コース予約 (飲食) とは別導線。
 */
export default function ExperienceSection({ products, trialStore }: { products: ExperienceProduct[]; trialStore?: boolean }) {
  const navigate = useNavigate()
  const { t, locale } = useI18n()

  if (products.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <Ticket size={32} className="mx-auto text-text-tertiary mb-2" />
        <p className="font-body text-sm text-text-secondary">{t('experienceSection.empty')}</p>
      </div>
    )
  }

  return (
    <div className="px-4 py-4 space-y-3">
      {products.map((p) => {
        const name = locale === 'ja' ? p.name : p.nameEn || p.name
        const desc = locale === 'ja' ? p.description : p.descriptionEn || p.description
        const timed = p.redeemPolicy === 'timed'
        return (
          <div key={p.id} className="rounded-2xl bg-white border border-gray-100 shadow-card overflow-hidden">
            {p.imageUrl && <img src={p.imageUrl} alt="" className="h-36 w-full object-cover" />}
            <div className="p-4">
              <div className="flex items-start gap-2">
                <p className="flex-1 min-w-0 font-display text-base font-semibold text-text-primary">{name}</p>
                <span className="shrink-0 flex flex-col items-end gap-1">
                  {/* #1482 送迎の有無を**アイコンで**出す。提案の中心は「車がなくても
                    * 町を回れる」で、説明文に埋もれると売り物の核が伝わらない。 */}
                  {p.transportIncluded && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-matcha/15 px-2 py-0.5 text-[10px] font-body font-semibold text-matcha">
                      <Car size={10} />
                      {t('experience.badgeTransport')}
                    </span>
                  )}
                  {/* #1487 いつ終わるか。映画の上映時間と同じで、**終わりが分からないと
                    * 予定が組めない**。
                    *
                    * ここで一番大事なのは「決まった長さ」と「平均の滞在時間」を
                    * **読み分けられること**。同じ「約2時間」で出すと、時間で追い出されるのか
                    * 好きなだけ居られるのかが分からない。時計 = 決まっている、∞ = 自由。 */}
                  {p.openEnded ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-matcha/15 px-2 py-0.5 text-[10px] font-body font-semibold text-matcha">
                      <InfinityIcon size={10} />
                      {t('experience.openEnded')}
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-body font-semibold ${timed ? 'bg-navy/10 text-navy' : 'bg-gray-100 text-gray-600'}`}>
                      {timed && <Clock size={10} />}
                      {p.durationMinutes
                        ? t('experience.durationFixed', { n: formatDuration(p.durationMinutes, t) })
                        : timed ? t('experience.badgeTimed') : t('experience.scheduleFree')}
                    </span>
                  )}
                  {/* 自由でも目安は出す — 「自由」だけだと何時間見ておけばよいか分からない */}
                  {p.openEnded && !!p.durationMinutes && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-body font-medium text-gray-600">
                      {t('experience.durationEstimate', { n: formatDuration(p.durationMinutes, t) })}
                    </span>
                  )}
                </span>
              </div>
              {desc && <p className="mt-1 font-body text-xs text-text-secondary line-clamp-2">{desc}</p>}
              <div className="mt-3 flex items-center justify-between">
                <span className="font-display text-lg font-bold text-navy">¥{p.priceJpy.toLocaleString()}</span>
                <button
                  onClick={() => navigate(experienceBuyPath(p.id, trialStore))}
                  className="tap-effect inline-flex items-center gap-1.5 rounded-full bg-yokoso-red px-4 py-2 text-xs font-body font-semibold text-white"
                >
                  <Ticket size={14} />
                  {t('storeDetail.buyTicket')}
                </button>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
